import {
    answerCall,
    hangupSession,
    mutePhone,
    holdCall,
    unholdCall,
} from 'flow/actions/phone-actions';
import { unregisterHeadset } from 'flow/actions/headset-actions';
import { addHeadsetLogEntry } from 'flow/utils/browser-utils';
import {
    areHeadsetsDisabledBecauseOfMultipleCalls,
    selectIsHeadsetControlEnabled,
} from 'flow/selectors/headset-selectors';

/*
 * AC-811: Defence-in-depth — check the master headset toggle before acting on
 * any SDK event. Previously only areHeadsetsDisabledBecauseOfMultipleCalls was
 * checked, but headset SDKs (Jabra, EPOS, Plantronics, Yealink) can still fire
 * hardware events even after Main.tsx stops polling / unregisters the headset.
 * Checking selectIsHeadsetControlEnabled here stops stale SDK events from
 * dispatching actions (especially answerCall) when the user has disabled
 * headset control, preventing the double-dispatch bug that breaks call audio.
 *
 * Note: onDisconnect is intentionally excluded from this guard — we always want
 * to clean up a disconnected headset regardless of the toggle state.
 */
const isHeadsetActionAllowed = (getState, headsetId, eventName) => {
    if (!selectIsHeadsetControlEnabled(getState())) {
        addHeadsetLogEntry(
            `Skipping ${eventName} from headset ${headsetId} — headset control is disabled`,
        );
        return false;
    }
    if (areHeadsetsDisabledBecauseOfMultipleCalls(getState())) {
        addHeadsetLogEntry(
            `Skipping ${eventName} from headset ${headsetId} because of multiple calls`,
        );
        return false;
    }
    return true;
};

export const HeadsetCallbacks = (dispatch, getState) => {
    return (headsetId) => {
        const guardAction = (eventName) =>
            isHeadsetActionAllowed(getState, headsetId, eventName);

        return headsetLoggingDecorator({
            onMuteEvent: () => {
                if (guardAction('mute event')) {
                    dispatch(mutePhone(true));
                }
            },
            onUnmuteEvent: () => {
                if (guardAction('unmute event')) {
                    dispatch(mutePhone(false));
                }
            },
            onAcceptCall: () => {
                if (guardAction('accept call event')) {
                    dispatch(answerCall(undefined, true));
                }
            },
            onHangup: () => {
                // We're not supplying a call-id to hangup when we use headsets.
                // We only use headsets when we're signaling using JsSip, and the
                // activecall-key is used only when we do the signaling through Top.
                if (guardAction('hangup event')) {
                    dispatch(hangupSession(undefined, true));
                }
            },
            onDisconnect: () => {
                dispatch(unregisterHeadset(headsetId));
            },
            onHoldCall: () => {
                if (guardAction('hold call event')) {
                    dispatch(holdCall());
                }
            },
            onUnholdCall: () => {
                if (guardAction('unhold call event')) {
                    dispatch(unholdCall());
                }
            },
        });
    };
};

const headsetLoggingDecorator = (fcns) => {
    const addLogging = (f) => {
        return (...args) => {
            addHeadsetLogEntry(
                `Headset -> app: executing callback '${f.name}'`,
            );
            return f(...args);
        };
    };
    const loggingCallbacks = Object.keys(fcns).reduce((acc, fcnName) => {
        acc[fcnName] = addLogging(fcns[fcnName]);
        return acc;
    }, {});
    return loggingCallbacks;
};
